import React, { useState, useEffect, useRef, useMemo } from "react";
import { FileText, Clock3, AlertCircle, ChevronDown, Check, FileUp, Search, ChevronLeft, ChevronRight, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Sidebar from "./Sidebar";
import Header from "./Header";
import { request } from "../service/HttpService";
import { calculateOverallScore } from "../service/Operations";

function pageWindows(current, total) {
    if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
    const show = new Set([1, total, current - 1, current, current + 1].filter(n => n >= 1 && n <= total));
    const sorted = [...show].sort((a, b) => a - b);
    const result = [];
    for (let i = 0; i < sorted.length; i++) {
        if (i > 0 && sorted[i] - sorted[i - 1] > 1) result.push(null);
        result.push(sorted[i]);
    }
    return result;
}

function StatusBadge({ status }) {
    if (!status) return null;
    const s = status.toLowerCase();
    const isComplete = s === "completed";
    const isError = s === "error";
    const cls = isComplete ? "badge-complete" : isError ? "badge-error" : "badge-processing";
    const Icon = isComplete ? CheckCircle : isError ? AlertCircle : Clock3;
    const label = isComplete ? "Complete" : isError ? "Error" : "In Process";
    return (
        <span className={`status-badge ${cls}`}>
            <Icon size={12} />
            {label}
        </span>
    );
}

export default function Submissions() {
    const navigate = useNavigate();
    const [manuscripts, setManuscripts] = useState([]);
    const [loading, setLoading] = useState(true);

    const [search, setSearch] = useState("");
    const [page, setPage] = useState(1);
    const PAGE_SIZE = 10;

    const [typeFilter, setTypeFilter] = useState("ALL");
    const [typeOpen, setTypeOpen] = useState(false);
    const typeRef = useRef(null);

    const [dateFilter, setDateFilter] = useState("ALL");
    const [dateOpen, setDateOpen] = useState(false);
    const dateRef = useRef(null);
    const [customStart, setCustomStart] = useState("");
    const [customEnd, setCustomEnd] = useState("");
    const [appliedStart, setAppliedStart] = useState("");
    const [appliedEnd, setAppliedEnd] = useState("");

    const hasError = useMemo(() => manuscripts.some(m => m.status?.toLowerCase() === "error"), [manuscripts]);

    useEffect(() => {
        request("GET", "/manuscript/all")
            .then(res => setManuscripts(res.manuscripts ?? []))
            .catch(() => {})
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        const onDocClick = (e) => {
            if (typeRef.current && !typeRef.current.contains(e.target)) setTypeOpen(false);
            if (dateRef.current && !dateRef.current.contains(e.target)) setDateOpen(false);
        };
        document.addEventListener("click", onDocClick);
        return () => document.removeEventListener("click", onDocClick);
    }, []);

    const fmt = s => new Date(s + "T00:00:00").toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

    const dateLabel = useMemo(() => {
        if (dateFilter === "ALL") return "All Time";
        if (dateFilter === "7D") return "Last 7 days";
        if (dateFilter === "30D") return "Last 30 days";
        if (dateFilter === "CUSTOM") {
            if (appliedStart && appliedEnd) return `${fmt(appliedStart)} – ${fmt(appliedEnd)}`;
            return "Custom…";
        }
        return "All Time";
    }, [dateFilter, appliedStart, appliedEnd]);

    const parseDate = (d) => {
        if (!d) return NaN;
        const n = Number(d);
        return isNaN(n) ? new Date(d).getTime() : n;
    };

    useEffect(() => { setPage(1); }, [search, typeFilter, dateFilter, appliedStart, appliedEnd]);

    const visible = useMemo(() => {
        let list = [...manuscripts].sort((a, b) => (b.uploadDate ?? 0) - (a.uploadDate ?? 0));

        if (search) {
            const q = search.toLowerCase();
            list = list.filter(m =>
                (m.title ?? "").toLowerCase().includes(q) ||
                (m.name ?? "").toLowerCase().includes(q) ||
                (m.authors ?? "").toLowerCase().includes(q)
            );
        }

        if (typeFilter === "COMPLETED") list = list.filter(m => m.status?.toLowerCase() === "completed");
        else if (typeFilter === "PENDING") list = list.filter(m => { const s = m.status?.toLowerCase(); return s !== "completed" && s !== "error"; });
        else if (typeFilter === "ERROR") list = list.filter(m => m.status?.toLowerCase() === "error");

        if (dateFilter === "ALL") return list;
        const now = Date.now();
        const DAY = 24 * 60 * 60 * 1000;
        let start = null, end = null;
        if (dateFilter === "7D") { start = now - 7 * DAY; end = now; }
        else if (dateFilter === "30D") { start = now - 30 * DAY; end = now; }
        else if (dateFilter === "CUSTOM") {
            if (!appliedStart || !appliedEnd) return list;
            start = new Date(appliedStart + "T00:00:00").getTime();
            const e = new Date(appliedEnd + "T00:00:00"); e.setHours(23, 59, 59, 999); end = e.getTime();
            if (start > end) { const t = start; start = end; end = t; }
        }
        return list.filter(m => {
            const t = m.uploadDate;
            if (!t) return false;
            if (start !== null && t < start) return false;
            if (end !== null && t > end) return false;
            return true;
        });
    }, [manuscripts, search, typeFilter, dateFilter, appliedStart, appliedEnd]);

    const totalPages = Math.max(1, Math.ceil(visible.length / PAGE_SIZE));
    const safePage = Math.min(page, totalPages);
    const paginated = visible.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

    return (
        <div className="home-layout">
            <Sidebar activePage="Submissions" />

            <main className="home-main">
                <Header />

                <div className="home-content">
                        <div className="upload-main">
                            <div className="upload-page-title-row">
                                <h1 className="upload-page-heading">Submissions</h1>
                            </div>

                            {/* First-time empty state */}
                            {!loading && manuscripts.length === 0 && (
                                <div className="submissions-empty submissions-empty-first">
                                    <FileText size={40} className="submissions-empty-icon" />
                                    <p className="submissions-empty-title">No papers yet</p>
                                    <p className="submissions-empty-sub">Upload your first paper to get started.</p>
                                    <div style={{ display: "flex", gap: 10, marginTop: 16, flexWrap: "wrap", justifyContent: "center" }}>
                                        <button className="start-analysis-btn" onClick={() => navigate("/upload")}>
                                            Upload Your First Paper
                                        </button>
                                        <button className="upload-btn" style={{ background: "transparent", color: "var(--ink-600)", border: "1px solid var(--border)" }} onClick={() => navigate("/sample/submission")}>
                                            View Sample Report
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Filters */}
                            {!loading && manuscripts.length > 0 && (
                                <div className="submissions-filters">
                                    {/* Type dropdown */}
                                    <div className="similar-works-filter" ref={typeRef}>
                                        <div
                                            className="similar-works-dropdown"
                                            role="button"
                                            tabIndex={0}
                                            aria-haspopup="listbox"
                                            aria-expanded={typeOpen}
                                            onClick={() => setTypeOpen(o => !o)}
                                            onKeyDown={e => {
                                                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setTypeOpen(o => !o); }
                                                if (e.key === "Escape") setTypeOpen(false);
                                            }}
                                        >
                                            <span className="similar-works-dropdown-label">
                                                {{ ALL: "All Types", COMPLETED: "Completed", PENDING: "Pending", ERROR: "Error" }[typeFilter]}
                                            </span>
                                            <ChevronDown size={14} className={`similar-works-chevron${typeOpen ? " open" : ""}`} />

                                            {typeOpen && (
                                                <ul className="similar-works-dropdown-menu" role="listbox" onClick={e => e.stopPropagation()}>
                                                    {[
                                                        { val: "ALL", label: "All Types" },
                                                        { val: "COMPLETED", label: "Completed" },
                                                        { val: "PENDING", label: "Pending" },
                                                        ...(hasError ? [{ val: "ERROR", label: "Error" }] : []),
                                                    ].map(opt => (
                                                        <li
                                                            key={opt.val}
                                                            role="option"
                                                            aria-selected={typeFilter === opt.val}
                                                            className={`similar-works-dropdown-item${typeFilter === opt.val ? " active" : ""}`}
                                                            onClick={() => { setTypeFilter(opt.val); setTypeOpen(false); }}
                                                        >
                                                            <span>{opt.label}</span>
                                                            {typeFilter === opt.val && <Check size={13} />}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>

                                    {/* Date dropdown */}
                                    <div className="similar-works-filter" ref={dateRef}>
                                        <div
                                            className="similar-works-dropdown"
                                            role="button"
                                            tabIndex={0}
                                            aria-haspopup="listbox"
                                            aria-expanded={dateOpen}
                                            onClick={() => setDateOpen(o => !o)}
                                            onKeyDown={e => {
                                                if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setDateOpen(o => !o); }
                                                if (e.key === "Escape") setDateOpen(false);
                                            }}
                                        >
                                            <span className="similar-works-dropdown-label">{dateLabel}</span>
                                            <ChevronDown size={14} className={`similar-works-chevron${dateOpen ? " open" : ""}`} />

                                            {dateOpen && (
                                                <ul className="similar-works-dropdown-menu" role="listbox" onClick={e => e.stopPropagation()} style={{ minWidth: 220 }}>
                                                    {[
                                                        { val: "ALL", label: "All Time" },
                                                        { val: "7D", label: "Last 7 days" },
                                                        { val: "30D", label: "Last 30 days" },
                                                        { val: "CUSTOM", label: "Custom…" },
                                                    ].map(opt => (
                                                        <li
                                                            key={opt.val}
                                                            role="option"
                                                            aria-selected={dateFilter === opt.val}
                                                            className={`similar-works-dropdown-item${dateFilter === opt.val ? " active" : ""}`}
                                                            onClick={() => {
                                                                setDateFilter(opt.val);
                                                                if (opt.val !== "CUSTOM") setDateOpen(false);
                                                            }}
                                                        >
                                                            <span>{opt.label}</span>
                                                            {dateFilter === opt.val && opt.val !== "CUSTOM" && <Check size={13} />}
                                                        </li>
                                                    ))}

                                                    {dateFilter === "CUSTOM" && (
                                                        <li className="similar-works-dropdown-item" style={{ display: "block", borderTop: "1px solid var(--border-subtle)", background: "var(--surface)" }} onClick={e => e.stopPropagation()}>
                                                            <div style={{ display: "grid", gap: 6 }}>
                                                                <label style={{ fontSize: 11, color: "var(--ink-400)" }}>Start date</label>
                                                                <input type="date" value={customStart} onChange={e => setCustomStart(e.target.value)} className="sub-date-input" />
                                                                <label style={{ fontSize: 11, color: "var(--ink-400)", marginTop: 4 }}>End date</label>
                                                                <input type="date" value={customEnd} onChange={e => setCustomEnd(e.target.value)} className="sub-date-input" />
                                                                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 8 }}>
                                                                    <button className="sub-date-clear" onClick={() => { setCustomStart(""); setCustomEnd(""); setAppliedStart(""); setAppliedEnd(""); }}>Clear</button>
                                                                    <button className="sub-date-apply" disabled={!customStart || !customEnd} onClick={() => { setAppliedStart(customStart); setAppliedEnd(customEnd); setDateOpen(false); }}>Apply</button>
                                                                </div>
                                                            </div>
                                                        </li>
                                                    )}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                    <div className="submissions-search-wrap" style={{ position: "relative", marginLeft: "auto" }}>
                                        <Search size={13} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--ink-300)", pointerEvents: "none" }} />
                                        <input
                                            type="text"
                                            placeholder="Search…"
                                            value={search}
                                            onChange={e => setSearch(e.target.value)}
                                            style={{ paddingLeft: 30, paddingRight: search ? 26 : 10, paddingTop: 7, paddingBottom: 7, border: "1px solid var(--border)", borderRadius: 8, fontSize: 13, background: "var(--surface-2)", outline: "none", width: 160 }}
                                        />
                                        {search && (
                                            <button onClick={() => setSearch("")} style={{ position: "absolute", right: 8, top: "50%", transform: "translateY(-50%)", background: "none", border: "none", cursor: "pointer", color: "var(--ink-300)", fontSize: 16, lineHeight: 1 }}>×</button>
                                        )}
                                    </div>
                                    <button className="upload-btn" style={{ border: "1px solid var(--border)" }} onClick={() => navigate("/upload")}>
                                        <FileUp size={14} />
                                        Upload Paper
                                    </button>
                                </div>
                            )}

                            {!loading && totalPages > 1 && (
                                <div className="pagination-bar" style={{ marginBottom: 8 }}>
                                    <p className="pagination-count">{(safePage - 1) * PAGE_SIZE + 1}–{Math.min(safePage * PAGE_SIZE, visible.length)} of {visible.length}</p>
                                    <div className="pagination-controls">
                                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={safePage === 1} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === 1 ? "var(--surface-3)" : "var(--surface)", cursor: safePage === 1 ? "default" : "pointer", fontSize: 12, color: safePage === 1 ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><ChevronLeft size={13} /><span className="pagination-btn-text">Previous</span></button>
                                        <div className="pagination-pages">
                                            {pageWindows(safePage, totalPages).map((n, i) =>
                                                n === null
                                                    ? <span key={`e${i}`} style={{ padding: "5px 2px", fontSize: 12, color: "var(--ink-300)" }}>…</span>
                                                    : <button key={n} onClick={() => setPage(n)} style={{ padding: "5px 10px", borderRadius: 7, border: "1px solid var(--border)", background: n === safePage ? "var(--brand)" : "var(--surface)", color: n === safePage ? "white" : "var(--ink-700)", cursor: "pointer", fontSize: 12, fontWeight: n === safePage ? 600 : 400 }}>{n}</button>
                                            )}
                                        </div>
                                        <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={safePage === totalPages} style={{ padding: "5px 12px", borderRadius: 7, border: "1px solid var(--border)", background: safePage === totalPages ? "var(--surface-3)" : "var(--surface)", cursor: safePage === totalPages ? "default" : "pointer", fontSize: 12, color: safePage === totalPages ? "var(--ink-200)" : "var(--ink-700)", display: "flex", alignItems: "center", gap: 4, flexShrink: 0 }}><span className="pagination-btn-text">Next</span><ChevronRight size={13} /></button>
                                    </div>
                                    <div className="pagination-spacer" />
                                </div>
                            )}

                            {/* Submissions list */}
                            {(loading || manuscripts.length > 0) && <div className="sub-list">
                                {loading ? (
                                    Array.from({ length: 4 }).map((_, i) => (
                                        <div key={i} className="sub-card sub-card-skeleton">
                                            <div className="skeleton skeleton-icon" />
                                            <div className="sub-info">
                                                <div className="skeleton skeleton-title" />
                                                <div className="skeleton skeleton-authors" />
                                            </div>
                                            <div className="sub-meta">
                                                <div className="skeleton skeleton-date" />
                                            </div>
                                        </div>
                                    ))
                                ) : visible.length === 0 ? (
                                    <div className="submissions-empty">
                                        <FileText size={32} className="submissions-empty-icon" />
                                        <p className="submissions-empty-title">No submissions found</p>
                                        <p className="submissions-empty-sub">{typeFilter !== "ALL" || dateFilter !== "ALL" ? "Try adjusting your filters." : "Upload a paper to get started."}</p>
                                        {typeFilter === "ALL" && dateFilter === "ALL" && (
                                            <button className="start-analysis-btn" style={{ marginTop: 12 }} onClick={() => navigate("/upload")}>Upload a Paper</button>
                                        )}
                                    </div>
                                ) : paginated.map((item) => {
                                    const score = calculateOverallScore(item);
                                    const title = (item.title?.trim()) || item.name?.replace(/\.[^/.]+$/, "") || "Untitled";
                                    const authors = item.authors?.trim() || "Unknown Authors";
                                    const s = item.status?.toLowerCase();
                                    const isComplete = s === "completed";
                                    const isError = s === "error";
                                    return (
                                        <div key={item.id} className="sub-card" onClick={() => navigate(`/submission?id=${item.id}`)} style={{ cursor: "pointer" }}>
                                            <div className="sub-icon">
                                                <FileText size={15} />
                                            </div>
                                            <div className="sub-info">
                                                <p className="sub-title">{title}</p>
                                                <p className="sub-authors">{authors}</p>
                                                {isComplete && (
                                                    <div className="sub-scores-row">
                                                        {item.noveltyScore != null && <span className="sub-score-chip sub-score-chip-n">N: {item.noveltyScore}</span>}
                                                        {item.methodScore != null && <span className="sub-score-chip sub-score-chip-m">M: {item.methodScore}</span>}
                                                        {item.impactScore != null && <span className="sub-score-chip sub-score-chip-i">I: {item.impactScore}</span>}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="sub-meta">
                                                <p className="sub-date">{item.uploadDate ? new Date(item.uploadDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : ""}</p>
                                                <div className="sub-right">
                                                    <StatusBadge status={item.status} />
                                                    {isComplete && score > 0 && <span className="sub-score">{score}</span>}
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>}
                        </div>
                </div>
            </main>
        </div>
    );
}
